import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, KeyRound, ShieldAlert } from "lucide-react";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { cn } from "@/lib/utils";

interface CredentialShellProps {
  readonly id: string;
  readonly title: string;
  readonly children: ReactNode;
}

/** Moldura padrao das secoes de credencial (mesmo visual de Minha conta). */
export function CredentialShell({ id, title, children }: CredentialShellProps): JSX.Element {
  return (
    <section id={`platform-${id}`} className="overflow-hidden rounded-xl border border-border bg-card" aria-labelledby={`section-${id}`}>
      <div className="flex items-center gap-2.5 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
        <KeyRound size={14} className="text-muted-foreground" />
        <h2 id={`section-${id}`} className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </h2>
      </div>
      <div className="space-y-5 p-5">{children}</div>
    </section>
  );
}

const STATUS_TEXT: Record<string, string> = {
  valid: "Conectada",
  invalid: "Recusada",
  pending: "Pendente",
  needs_approval: "Aguardando aprovação",
  disabled: "Desativada",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/** Saude da credencial no card: status, precisa reconectar e ultimo erro. */
export function CredentialHealth({ credential }: { credential: AffiliateCredentialPublic | undefined }): JSX.Element | null {
  if (!credential) {
    return null;
  }
  const needsReauth = credential.needs_reauth === true;
  const healthy = credential.status === "valid" && !needsReauth;
  const Icon = healthy ? CheckCircle2 : needsReauth ? ShieldAlert : AlertTriangle;
  return (
    <div
      className={cn(
        "space-y-1 rounded-lg border px-3 py-2 text-xs",
        healthy ? "border-primary/25 bg-primary/5 text-primary" : "border-destructive/30 bg-destructive/10 text-destructive",
      )}
      data-testid="credential-health"
    >
      <p className="flex items-center gap-1.5 font-semibold">
        <Icon size={13} />
        {needsReauth ? "Precisa reconectar" : STATUS_TEXT[credential.status] ?? credential.status}
        {credential.last_validated_at && (
          <span className="font-normal text-muted-foreground">· testada em {formatDate(credential.last_validated_at)}</span>
        )}
      </p>
      {credential.validation_error && <p className="break-words">Último erro: {credential.validation_error}</p>}
    </div>
  );
}

export function SaveFeedback({ status, error }: { status: string | null; error: string | null }): JSX.Element | null {
  if (!status && !error) {
    return null;
  }
  return (
    <p role={error ? "alert" : "status"} className={cn("text-sm", error ? "text-destructive" : "text-primary")}>
      {error ?? status}
    </p>
  );
}
